"use client";

import { useEffect, useRef, useState } from "react";

interface GenreTagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  options: string[];
}

export function GenreTagInput({ tags, onChange, options }: GenreTagInputProps) {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = input
    ? options.filter(
        (o) =>
          o.toLowerCase().includes(input.toLowerCase()) &&
          !tags.some((t) => t.toLowerCase() === o.toLowerCase())
      )
    : options.filter(
        (o) => !tags.some((t) => t.toLowerCase() === o.toLowerCase())
      );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIndex] as HTMLElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex]);

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !tags.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      onChange([...tags, trimmed]);
    }
    setInput("");
    setOpen(false);
    setHighlightIndex(-1);
  };

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < filtered.length) {
        addTag(filtered[highlightIndex]);
      } else if (input.trim()) {
        addTag(input);
      }
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      removeTag(tags.length - 1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <div className="flex flex-wrap gap-1 rounded border border-neutral-700 bg-transparent px-1.5 py-1 focus-within:border-acid">
        {tags.map((tag, i) => (
          <span
            key={i}
            className="flex items-center gap-1 rounded bg-acid/20 px-1.5 py-0.5 font-mono text-[10px] text-acid"
          >
            {tag}
            <button
              onClick={() => removeTag(i)}
              className="text-acid/60 hover:text-acid"
            >
              &times;
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
            setHighlightIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? "Add genre..." : ""}
          className="min-w-[80px] flex-1 bg-transparent py-0.5 font-mono text-xs text-neutral-200 placeholder:text-neutral-600 focus:outline-none"
        />
      </div>
      {open && filtered.length > 0 && (
        <ul
          ref={listRef}
          className="absolute left-0 right-0 top-full z-[210] mt-1 max-h-40 overflow-y-auto rounded border border-neutral-700 bg-neutral-900 py-1"
        >
          {filtered.slice(0, 20).map((option, i) => (
            <li
              key={option}
              onMouseDown={() => addTag(option)}
              onMouseEnter={() => setHighlightIndex(i)}
              className={`cursor-pointer px-2 py-1 font-mono text-xs ${
                i === highlightIndex
                  ? "bg-acid/20 text-acid"
                  : "text-neutral-300 hover:bg-neutral-800"
              }`}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
